import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import axiosInstance from "services/AxiosInstance";
import {
  notifyError,
  notifySuccess,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import Tables from "jsx/components/CitoPlus/components/tables";
import Select from "react-select";
import ZonaSocialSelectReact from "jsx/components/CitoPlus/components/ZonaSocial/ZonaSocialSelectReact";
import ActivoCreateForm from "./ActivoCreateForm";
import ActivoHistoryModal from "./ActivoHistoryModal";
import ActivosImportar from "./ActivosImportar";
import swal from "sweetalert";
import { ButtonGroup } from "react-bootstrap";
import {
  CitoPlusLinkIconPrimary,
  CitoPlusLinkIconDanger,
  CitoPlusLinkIconSecondary,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import moment from "moment";

const ACTIVOS_URL = "/api/v3/zonas-sociales/activos/";
const CATEGORIAS_URL = "/api/v3/zonas-sociales/activos/categorias/";

const ESTADO_OPTIONS = [
  { value: "all", label: "Todos los estados" },
  { value: "nuevo", label: "Nuevo" },
  { value: "bueno", label: "Bueno" },
  { value: "reparacion", label: "En reparación" },
  { value: "dado_de_baja", label: "Dado de baja" },
];

const ActivosList = () => {
  const [activos, setActivos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [zonaFilter, setZonaFilter] = useState("all");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermDebounced, setSearchTermDebounced] = useState("");

  const [categorias, setCategorias] = useState([]);
  const [isLoadingCategorias, setIsLoadingCategorias] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedActivo, setSelectedActivo] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Ref para mantener valores actuales sin causar re-renders
  const filtersRef = useRef({
    zonaFilter: "all",
    estadoFilter: "all",
    categoriaFilter: "all",
    searchTermDebounced: "",
    sortField: "",
    order: "asc",
  });

  // Actualizar refs cuando cambien los valores
  useEffect(() => {
    filtersRef.current = {
      zonaFilter,
      estadoFilter,
      categoriaFilter,
      searchTermDebounced,
      sortField,
      order,
    };
  }, [
    zonaFilter,
    estadoFilter,
    categoriaFilter,
    searchTermDebounced,
    sortField,
    order,
  ]);

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTermDebounced(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCategorias = useCallback(async () => {
    setIsLoadingCategorias(true);
    try {
      const response = await axiosInstance.get(CATEGORIAS_URL);
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setCategorias(results);
    } catch (error) {
      notifyError("No fue posible obtener las categorías de activos.");
      setCategorias([]);
    } finally {
      setIsLoadingCategorias(false);
    }
  }, []);

  const fetchActivos = useCallback(
    async (pageOrOptions = 1) => {
      const filters = filtersRef.current;

      const page =
        typeof pageOrOptions === "number"
          ? pageOrOptions
          : pageOrOptions?.page || currentPage || 1;

      const urlOverride =
        typeof pageOrOptions === "object" && pageOrOptions?.url
          ? pageOrOptions.url
          : null;

      setIsLoading(true);
      try {
        const params = {};

        if (!urlOverride) {
          params.page = page;
          if (filters.zonaFilter !== "all") {
            params.zona = filters.zonaFilter;
          }
          if (filters.estadoFilter !== "all") {
            params.estado = filters.estadoFilter;
          }
          if (filters.categoriaFilter !== "all") {
            params.categoria = filters.categoriaFilter;
          }
          if (filters.searchTermDebounced.trim()) {
            params.search = filters.searchTermDebounced.trim();
          }

          if (filters.sortField) {
            params.ordering = `${filters.order === "desc" ? "-" : ""}${filters.sortField}`;
          }
        }

        const response = await axiosInstance.get(
          urlOverride || ACTIVOS_URL,
          urlOverride ? {} : { params },
        );

        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        setActivos(payload);

        const count =
          typeof response.data?.count === "number"
            ? response.data.count
            : Array.isArray(response.data)
              ? response.data.length
              : payload.length;

        const fetchedTotalPages =
          response.data?.total_pages ??
          (count > 0 ? Math.ceil(count / (response.data?.page_size ?? 25)) : 1);

        setTotalRecords(count);
        setTotalPages(Math.max(fetchedTotalPages, 1));
        const resolvedPage =
          response.data?.current_page ?? response.data?.page ?? page;
        setCurrentPage(resolvedPage);
        setNextUrl(response.data?.next ?? null);
        setPreviousUrl(response.data?.previous ?? null);
      } catch (error) {
        notifyError(
          "No fue posible obtener el inventario de activos. Intenta de nuevo más tarde.",
        );
        setActivos([]);
        setTotalRecords(0);
        setTotalPages(1);
        setCurrentPage(1);
        setNextUrl(null);
        setPreviousUrl(null);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage],
  );

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  // Consolidar efectos de filtros en uno solo
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchActivos(1);
    }, 0);
    return () => clearTimeout(timer);
  }, [zonaFilter, estadoFilter, categoriaFilter, searchTermDebounced]);

  const handleZonaChange = useCallback((newValue) => {
    setZonaFilter(newValue || "all");
  }, []);

  const handleEstadoChange = useCallback((event) => {
    setEstadoFilter(event.target.value);
  }, []);

  const handleCategoriaChange = useCallback((event) => {
    setCategoriaFilter(event.target.value);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleResetFilters = useCallback(() => {
    setZonaFilter("all");
    setEstadoFilter("all");
    setCategoriaFilter("all");
    setSearchTerm("");
    setSearchTermDebounced("");
    setSortField("");
    setOrder("asc");
    fetchActivos(1);
  }, [fetchActivos]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = {};
      if (zonaFilter !== "all") params.zona = zonaFilter;
      if (estadoFilter !== "all") params.estado = estadoFilter;
      if (categoriaFilter !== "all") params.categoria = categoriaFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await axiosInstance.get(`${ACTIVOS_URL}exportar/`, {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `activos-${moment().format("YYYY-MM-DD")}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifyError("No fue posible exportar los activos.");
    } finally {
      setIsExporting(false);
    }
  }, [zonaFilter, estadoFilter, categoriaFilter, searchTerm]);

  const handleConsult = useCallback(() => {
    fetchActivos(1);
  }, [fetchActivos]);

  const handleSortingChange = useCallback(
    (column) => {
      if (!column?.id) {
        return;
      }

      const nextOrder =
        sortField === column.id ? (order === "asc" ? "desc" : "asc") : "asc";

      setSortField(column.id);
      setOrder(nextOrder);

      fetchActivos(1);
    },
    [fetchActivos, order, sortField],
  );

  const handleViewHistory = useCallback((activo) => {
    setSelectedActivo(activo);
    setShowHistoryModal(true);
  }, []);

  const handleEdit = useCallback((activo) => {
    setSelectedActivo(activo);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback(
    (activo) => {
      swal({
        title: "¿Estás seguro?",
        text: `El activo "${activo.nombre}" será eliminado permanentemente.`,
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      }).then(async (willDelete) => {
        if (willDelete) {
          try {
            await axiosInstance.delete(`${ACTIVOS_URL}${activo.uuid}/`);
            notifySuccess("Activo eliminado correctamente.");
            fetchActivos(currentPage);
          } catch (error) {
            notifyError("No fue posible eliminar el activo.");
          }
        }
      });
    },
    [fetchActivos, currentPage],
  );

  const categoriaOptions = useMemo(
    () => [
      { value: "all", label: "Todas las categorías" },
      ...categorias.map((cat) => ({
        value: String(cat.id),
        label: cat.nombre,
      })),
    ],
    [categorias],
  );

  const columns = useMemo(
    () => [
      {
        Header: "Consecutivo",
        accessor: "consecutivo",
        disableFilters: true,
      },
      {
        Header: "Nombre",
        accessor: "nombre",
        disableFilters: true,
      },
      {
        Header: "Cantidad",
        accessor: "cantidad",
        disableFilters: true,
      },
      {
        Header: "Estado",
        accessor: "estado",
        disableFilters: true,
      },
      {
        Header: "Zona social",
        accessor: (row) => {
          return (
            <span>
              {row.zona_social_nombre}
              <br />
              <small className="text-muted"> {row?.zona?.nombre} </small>
            </span>
          );
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: "Categoría",
        accessor: (row) => {
          return (
            <span>
              {row.categoria_nombre}
              <br />
              <small className="text-muted"> {row?.categoria?.nombre} </small>
            </span>
          );
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: "Costo",
        accessor: "costo",
        disableFilters: true,
      },
      {
        Header: "Acciones",
        id: "acciones",
        disableFilters: true,
        disableSortBy: true,
        accessor: (row) => (
          <ButtonGroup aria-label="Acciones del activo">
            <CitoPlusLinkIconSecondary
              title="Ver historial"
              onClick={() => handleViewHistory(row)}
            >
              <i className="fas fa-history" />
            </CitoPlusLinkIconSecondary>
            <CitoPlusLinkIconPrimary
              title="Editar activo"
              onClick={() => handleEdit(row)}
            >
              <i className="fas fa-edit" />
            </CitoPlusLinkIconPrimary>
            <CitoPlusLinkIconDanger
              title="Eliminar activo"
              onClick={() => handleDelete(row)}
            >
              <i className="fas fa-trash-alt" />
            </CitoPlusLinkIconDanger>
          </ButtonGroup>
        ),
      },
    ],
    [handleViewHistory, handleEdit, handleDelete],
  );

  return (
    <section className="card">
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
          <div className="d-flex flex-column me-auto flex-grow-1">
            <h2 className="card-title mb-0">Inventario de activos</h2>
            <div className="text-muted small">
              Administra los activos del conjunto y filtra por zona, estado o
              categoría.
            </div>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              type="button"
              className="btn btn-primary btn-sm text-white"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fas fa-plus me-2" />
              Nuevo activo
            </button>
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-import me-2" />
              Importar
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Exportando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-file-excel me-2" />
                  Exportar
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm text-body"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm text-white"
              onClick={handleConsult}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Consultando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-search me-2" />
                  Consultar
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="card-body">
        <div className="row g-3 mb-4">
          <div className="col-lg-3 col-md-6">
            <ZonaSocialSelectReact
              id="zona-filter"
              name="zona"
              value={zonaFilter === "all" ? "" : zonaFilter}
              onChange={(newValue) => setZonaFilter(newValue || "all")}
              allowAllOption={false}
              allOptionLabel="Todas las zonas"
              showLabel
              label="Zona social"
              placeholder="Todas las zonas"
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="estado-filter" className="form-label">
              Estado
            </label>
            <Select
              id="estado-filter"
              name="estado"
              value={
                ESTADO_OPTIONS.find((opt) => opt.value === estadoFilter) || null
              }
              onChange={(option) => setEstadoFilter(option?.value || "all")}
              options={ESTADO_OPTIONS}
              isClearable
              isSearchable
              placeholder="Todos los estados"
              isDisabled={isLoading}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="categoria-filter" className="form-label">
              Categoría
            </label>
            <Select
              id="categoria-filter"
              name="categoria"
              value={
                categoriaOptions.find((opt) => opt.value === categoriaFilter) ||
                null
              }
              onChange={(option) => setCategoriaFilter(option?.value || "all")}
              options={categoriaOptions}
              isClearable
              isSearchable
              placeholder="Todas las categorías"
              isDisabled={isLoadingCategorias || isLoading}
              noOptionsMessage={() => "No hay categorías"}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="search-term" className="form-label">
              Búsqueda
            </label>
            <input
              id="search-term"
              type="text"
              className="form-control"
              placeholder="Nombre u observaciones"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={isLoading}
            />
          </div>
        </div>

        {isLoading && (
          <div className="py-5 text-center">
            <i className="fas fa-circle-notch fa-spin fa-2x mb-2" />
            <div>Cargando inventario de activos...</div>
          </div>
        )}

        {!isLoading && activos.length === 0 && (
          <div className="alert alert-light border" role="alert">
            No encontramos activos para los criterios seleccionados.
          </div>
        )}

        {!isLoading && activos.length > 0 && (
          <Tables
            columns={columns}
            data={activos}
            loading={isLoading}
            totalPages={totalPages}
            currentPage={currentPage}
            totalRecords={totalRecords}
            fetchUrl={fetchActivos}
            handleSortingChange={handleSortingChange}
            sortField={sortField}
            order={order}
            page={undefined}
            nextUrl={nextUrl}
            previousUrl={previousUrl}
          />
        )}
      </div>
      <ActivoCreateForm
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchActivos(1);
        }}
      />

      <ActivoCreateForm
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedActivo(null);
        }}
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedActivo(null);
          fetchActivos(currentPage);
        }}
        activeActivo={selectedActivo}
      />

      {showHistoryModal && selectedActivo && (
        <ActivoHistoryModal
          show={showHistoryModal}
          onHide={() => {
            setShowHistoryModal(false);
            setSelectedActivo(null);
          }}
          activo={selectedActivo}
        />
      )}

      <ActivosImportar
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          fetchActivos(1);
        }}
      />
    </section>
  );
};

export default ActivosList;
